import { Grid } from '@mui/material';
import { ODSText } from '@telekom-ods/react-ui-kit';
import { Formik, Form, type FormikHelpers } from 'formik';
import { type FC } from 'react';
import { useLocation, useNavigate } from 'react-router';
import * as Yup from 'yup';

import Button from '../Buttons/Button';
import { FormikDateTimeField } from '../Formik';

const nowDate = new Date().toISOString().slice(0, 16);

interface DateRange {
  from: string;
  to: string;
}

interface DateTimeFilterProps {
  dateRange: DateRange;
  setDateRange: (dateRange: DateRange) => void;
}

interface FormValues {
  fromDate: string;
  toDate: string;
}

const DateTimeFilter: FC<DateTimeFilterProps> = ({ dateRange, setDateRange }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialValues: FormValues = {
    fromDate: dateRange.from,
    toDate: dateRange.to,
  };

  const validationSchema = Yup.object({
    fromDate: Yup.date()
      .max(Yup.ref('toDate'), 'From date must be before To date')
      .required('Required'),
    toDate: Yup.date()
      .min(Yup.ref('fromDate'), 'To date must be after From date')
      .max(nowDate)
      .required('Required'),
  });

  const handleSubmit = (data: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const newSearchParams = new URLSearchParams(location.search);

    newSearchParams.set('from', data.fromDate);
    newSearchParams.set('to', data.toDate);

    const newUrl = `${location.pathname}?${newSearchParams.toString()}`;

    navigate(newUrl, { replace: true });
    setDateRange({ from: data.fromDate, to: data.toDate });
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid }) => (
        <Form>
          <Grid
            container
            alignItems='center'
          >
            <Grid item>
              <Grid
                item
                paddingBottom={0.2}
              >
                <ODSText
                  data-testid='caption'
                  className='p-text'
                >
                  Time Range:{' '}
                </ODSText>
              </Grid>
              <Grid
                container
                spacing={2}
              >
                <Grid item>
                  <FormikDateTimeField
                    name='fromDate'
                    label='From'
                    inputProps={{ max: nowDate }}
                  />
                </Grid>
                <Grid item>
                  <FormikDateTimeField
                    name='toDate'
                    label='To'
                    inputProps={{ max: nowDate }}
                  />
                </Grid>
                <Grid item>
                  <Button
                    type='submit'
                    label='Submit'
                    disabled={!isValid}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default DateTimeFilter;
